import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { useWallet } from "@/entities/wallet";
import { ROUTES } from "@/shared/config";
import { formatMoney, moneySign } from "@/shared/lib";
import { Button, RollingNumber } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/** Figma: `Wallet · Overview` (23:2). */
export function WalletOverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isPending, isError, refetch } = useWallet();

  if (isPending || isError) {
    return (
      <div className="flex flex-col gap-3 px-5 py-3">
        <TopBar title={t("screens.wallet")} subtitle={t("wallet.subtitle")} />
        {isError ? (
          <ScreenState
            tone="loss"
            icon="warningFill"
            title={t("states.error.title")}
            body={t("states.error.body")}
            action={{ label: t("states.error.action"), onClick: () => void refetch() }}
          />
        ) : (
          <ScreenSkeleton blocks={[160, 120, 100]} />
        )}
      </div>
    );
  }

  return (
    // Тот же каскад, что на главной: корневой экран вкладки собирается
    // сверху вниз (FE-13).
    <div className="stagger-children flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("screens.wallet")} subtitle={t("wallet.subtitle")} />

      <section
        className="flex flex-col gap-1 rounded-3xl p-5"
        style={{ backgroundImage: "var(--gradient-balance)" }}
      >
        <span className="text-caption font-medium text-text-secondary">{t("wallet.balance")}</span>
        <RollingNumber
          value={formatMoney(data.balance, { suffix: "USDT" })}
          className="text-display font-extrabold text-text-primary"
        />
        <span className="text-caption font-normal text-text-secondary">
          {t("wallet.available")} {formatMoney(data.available_for_withdrawal, { suffix: "USDT" })}
        </span>
      </section>

      <dl className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <dt className="text-caption font-medium text-text-secondary">{t("wallet.invested")}</dt>
          <dd className="text-h3 font-semibold text-text-primary">
            {formatMoney(data.invested, { suffix: "USDT" })}
          </dd>
        </div>
        <div className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <dt className="text-caption font-medium text-text-secondary">{t("wallet.earned")}</dt>
          <dd className="text-h3 font-semibold text-text-primary">
            {formatMoney(data.earned_total, { sign: "always", suffix: "USDT" })}
          </dd>
        </div>
      </dl>

      {/* «Вывести» гаснет, когда выводить нечего: экран суммы всё равно упёрся
          бы в «доступно 0,00». Это сравнение с нулём, а не вычисление, —
          правило ТЗ №5 не нарушается.

          Порог берётся из `available_for_withdrawal`, а не из баланса: деньги,
          вложенные в роботов, на счету видны, но вывести их нельзя. */}
      <div className="flex flex-col gap-2">
        <Button size="cta" block onClick={() => void navigate(ROUTES.depositAsset)}>
          {t("wallet.deposit")}
        </Button>
        <Button
          size="cta"
          variant="secondary"
          block
          disabled={moneySign(data.available_for_withdrawal) <= 0}
          onClick={() => void navigate(ROUTES.withdrawAmount)}
        >
          {t("wallet.withdraw")}
        </Button>
      </div>

      <Link
        to={ROUTES.transactions}
        className="py-2 text-center text-caption font-semibold text-action-primary"
      >
        {t("wallet.history")} →
      </Link>
    </div>
  );
}
